import { gql } from "@apollo/client";
import { AccountFragment } from "fragments/account";

// One fragment, not two. A domain row is half a dozen short strings and there
// is no unbounded field on it, so the split that sections need — a listing
// fragment that leaves the body behind — would buy nothing here.
//
// `token` is read by the list, because the list IS the setup screen: the record
// to publish is what somebody comes to this screen for.
export const SiteDomainFragment = gql`
  fragment SiteDomainFragment on SiteDomain {
    id
    host
    siteId
    status
    token
    verifiedAt
    createdAt
    updatedAt
  }
`;

// Through the site, like everything else. That nesting is the tenancy boundary
// rather than a style: `site-domains-table` is keyed on a bare hostname and
// carries no tenant in its key, so the PATH to a row is what scopes it to one
// organization. See @common/schema/site-domain/fields.
export const list = gql`
  query SiteDomainList($siteId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            id
            domains {
              id
              list {
                id
                items {
                  ...SiteDomainFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${SiteDomainFragment}
`;

export const create = gql`
  mutation SiteDomainNew($siteId: String!, $input: SiteDomainInputCreate!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            domains {
              create(input: $input) {
                ...SiteDomainFragment
              }
            }
          }
        }
      }
    }
  }
  ${SiteDomainFragment}
`;

export const verify = gql`
  mutation SiteDomainVerify($siteId: String!, $host: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            domains {
              verify(host: $host) {
                ...SiteDomainFragment
              }
            }
          }
        }
      }
    }
  }
  ${SiteDomainFragment}
`;

export const remove = gql`
  mutation SiteDomainRemove($siteId: String!, $host: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            domains {
              remove(host: $host)
            }
          }
        }
      }
    }
  }
`;

// Every mutation here changes what the list shows — a create adds a row, a
// verify changes its status, a remove takes it away — so all three evict the
// same keys. The literal has to be a substring of what SiteDomainQueries.id
// resolves to on the API side — `<orgId>/<siteId>/site-domains/queries`, the
// site prefix being what keeps two sites' listings in two cache entries; if the
// two drift, the mutation still succeeds and the list keeps showing the old
// state, which is the silent staleness the page and section specs exist to catch.
export const update = () => (cache) => {
  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`site-domains/queries`)) {
      cache.data.delete(key);
    }
  });
};
